import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { Search, UserCheck, UserMinus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AdminListPage } from '@/features/admin/components/AdminListPage'
import { adminEventsService } from '../services/admin.events.service'
import { assignStaffToEventFn } from '@/lib/firebase/functions'
import type { EventDocument, UserDocument } from '@/types'

interface StaffUser extends UserDocument {
  id: string
}

export function AdminStaffManagementPage() {
  const [allUsers, setAllUsers] = useState<Array<UserDocument & { id: string }>>([])
  const [events, setEvents] = useState<Array<EventDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Edit dialog state
  const [editTarget, setEditTarget] = useState<StaffUser | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Remove staff dialog
  const [removeTarget, setRemoveTarget] = useState<StaffUser | null>(null)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [removing, setRemoving] = useState(false)

  const loadData = useCallback(() => {
    setLoading(true)
    setError(null)
    Promise.all([adminEventsService.getAllUsers(), adminEventsService.getAllEvents()])
      .then(([users, evs]) => {
        setAllUsers(users)
        setEvents(evs)
      })
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load data')),
      )
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const staffUsers = allUsers.filter((u) => u.role === 'staff')

  // Search results — find non-staff users matching the query
  const searchResults =
    searchQuery.length > 2
      ? allUsers.filter(
          (u) =>
            u.role !== 'staff' &&
            (u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (u.displayName ?? '').toLowerCase().includes(searchQuery.toLowerCase())),
        )
      : []

  function openEditDialog(user: StaffUser) {
    setEditTarget(user)
    setSelectedEventIds(user.assignedEvents ?? [])
    setSaveError(null)
    setEditDialogOpen(true)
  }

  async function handleSaveAssignment() {
    if (!editTarget) return
    setSaving(true)
    setSaveError(null)
    try {
      await assignStaffToEventFn({
        userId: editTarget.id,
        role: 'staff',
        assignedEvents: selectedEventIds,
      })
      loadData()
      setEditDialogOpen(false)
      setEditTarget(null)
    } catch (err: unknown) {
      setSaveError(formatUserFacingError(err, 'Failed to save'))
    } finally {
      setSaving(false)
    }
  }

  async function handlePromoteToStaff(user: UserDocument & { id: string }) {
    try {
      await assignStaffToEventFn({
        userId: user.id,
        role: 'staff',
        assignedEvents: [],
      })
      loadData()
    } catch (err: unknown) {
      setError(formatUserFacingError(err, 'Failed to promote user'))
    }
  }

  async function handleRemoveStaff() {
    if (!removeTarget) return
    setRemoving(true)
    try {
      await assignStaffToEventFn({
        userId: removeTarget.id,
        role: 'user',
        assignedEvents: [],
      })
      loadData()
      setRemoveDialogOpen(false)
      setRemoveTarget(null)
    } catch (err: unknown) {
      setError(formatUserFacingError(err, 'Failed to remove staff'))
    } finally {
      setRemoving(false)
    }
  }

  function toggleEventSelection(eventId: string) {
    setSelectedEventIds((prev) =>
      prev.includes(eventId) ? prev.filter((id) => id !== eventId) : [...prev, eventId],
    )
  }

  return (
    <>
      <AdminListPage
        title="Staff Management"
        error={error}
        loading={loading}
        skeletonRows={4}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Assigned Events</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {staffUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                  No staff members yet. Use the search below to find a user and promote them.
                </TableCell>
              </TableRow>
            ) : (
              staffUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    {user.displayName ?? 'No name'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{user.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(user.assignedEvents ?? []).length === 0 ? (
                        <span className="text-xs text-muted-foreground">None</span>
                      ) : (
                        (user.assignedEvents ?? []).map((eventId) => {
                          const event = events.find((e) => e.id === eventId)
                          return (
                            <Badge key={eventId} variant="secondary" className="text-xs">
                              {event?.title ?? eventId.slice(0, 8)}
                            </Badge>
                          )
                        })
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(user as StaffUser)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          setRemoveTarget(user as StaffUser)
                          setRemoveDialogOpen(true)
                        }}
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </AdminListPage>

      {/* Search to add new staff */}
      <div className="mt-8 space-y-4">
        <h2 className="text-xl font-semibold">Add Staff Member</h2>
        <p className="text-sm text-muted-foreground">
          Search for any user by email or name to promote them to staff.
        </p>
        <div className="flex gap-2 max-w-sm">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by email or name…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {searchResults.length > 0 && (
          <div className="rounded-md border max-w-lg">
            <Table>
              <TableBody>
                {searchResults.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <p className="font-medium text-sm">{user.displayName ?? 'No name'}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{user.role}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void handlePromoteToStaff(user)}
                      >
                        <UserCheck className="h-4 w-4 mr-1.5" />
                        Make Staff
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {searchQuery.length > 2 && searchResults.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No users found matching &ldquo;{searchQuery}&rdquo;.
          </p>
        )}
      </div>

      {/* Edit assignment dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Event Assignments</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Select events for{' '}
              <span className="font-semibold text-foreground">
                {editTarget?.displayName ?? editTarget?.email}
              </span>
            </p>
            {events.length === 0 ? (
              <p className="text-sm text-muted-foreground">No events available.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {events.map((event) => (
                  <label
                    key={event.id}
                    className="flex items-center gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/50"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={selectedEventIds.includes(event.id)}
                      onChange={() => toggleEventSelection(event.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{event.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {event.eventDate.toDate().toLocaleDateString()}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {saveError && <p className="text-sm text-destructive">{saveError}</p>}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditDialogOpen(false)
                setEditTarget(null)
              }}
            >
              Cancel
            </Button>
            <Button onClick={() => void handleSaveAssignment()} disabled={saving}>
              {saving ? 'Saving…' : 'Save Assignments'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove staff dialog */}
      <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Staff Access</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Remove staff access for{' '}
            <span className="font-semibold text-foreground">
              {removeTarget?.displayName ?? removeTarget?.email}
            </span>
            ? They will be reverted to a regular user.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRemoveDialogOpen(false)
                setRemoveTarget(null)
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleRemoveStaff()}
              disabled={removing}
            >
              {removing ? 'Removing…' : 'Remove Staff'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
